import React, { useEffect } from 'react';
import { Checkbox } from '@h2/ui';
import { Label } from '@h2/ui';
import { Textarea } from '@h2/ui';
import { useWorkSettingsStore } from '../../../stores/useWorkSettingsStore';
import { useConfigurationStore } from '../../../stores/useConfigurationStore';

interface LanguageSettingsStepProps {
  onNext?: () => void;
  onBack?: () => void;
  title?: string;
  description?: string;
  hideTitle?: boolean;
  isLanguageDisabled?: boolean; // 언어 선택 비활성화 여부
  preSelectedTargetLanguages?: string[]; // 미리 선택되어 있어야 하는 번역언어들 (등록 작업 기준)
}

interface LanguageSettingsData {
  sourceLanguage: string;
  targetLanguages: string[];
  tags: string[];
  memo: string;
}

export const LanguageSettingsStep: React.FC<LanguageSettingsStepProps> = ({
  title = "작업 설정 - 언어 및 기본 정보",
  description = "작업의 언어와 기본 정보를 설정합니다.",
  hideTitle = false,
  isLanguageDisabled = false,
  preSelectedTargetLanguages = []
}) => {
  // Configuration 스토어에서 실제 데이터 가져오기
  const { formData, updateField } = useConfigurationStore();
  
  // 언어 설정 데이터 (formData에서 직접 가져오기)
  const languageData = formData.languageSettingsComponent || {
    sourceLanguage: '',
    targetLanguages: [],
    tags: [],
    memo: ''
  };
  
  // WorkSettings 스토어 (UI 상태 관리용)
  const {
    markStepCompleted
  } = useWorkSettingsStore();

  // 언어 코드 변환 헬퍼 함수
  const convertLanguageNameToCode = (langName: string): string => {
    const langToCodeMap: { [key: string]: string } = {
      '한국어': 'ko',
      '영어': 'en', 
      '일본어': 'ja',
      '중국어': 'zh',
      '스페인어': 'es',
      '힌디어': 'hi'
    };
    return langToCodeMap[langName] || langName;
  };

  // 컴포넌트 마운트 시 디버깅 정보 출력
  useEffect(() => {
    console.log('🔍 LanguageSettingsStep 마운트/업데이트:', {
      isLanguageDisabled,
      preSelectedTargetLanguages,
      languageData,
      '전체 formData': useConfigurationStore.getState().formData
    });
  }, [isLanguageDisabled, preSelectedTargetLanguages, languageData]);
  
  const languages = [
    { code: 'ko', name: '한국어' },
    { code: 'en', name: '영어' },
    { code: 'ja', name: '일본어' },
    { code: 'zh', name: '중국어' },
    { code: 'es', name: '스페인어' },
    { code: 'hi', name: '힌디어' }
  ];
  
  // 원본언어 선택
  const selectSourceLanguage = (languageCode: string) => {
    // 선택된 원본언어를 타겟언어에서 제거
    const updatedTargetLanguages = (languageData.targetLanguages || []).filter(lang => lang !== languageCode);
    
    updateField('languageSettingsComponent', {
      ...languageData,
      sourceLanguage: languageCode,
      targetLanguages: updatedTargetLanguages
    });
  };

  // 번역언어 토글
  const toggleTargetLanguage = (languageCode: string) => {
    // 원본언어와 같은 언어는 선택 불가
    if (languageCode === languageData.sourceLanguage) {
      return;
    }
    
    const currentLanguages = languageData.targetLanguages || [];
    const updatedLanguages = currentLanguages.includes(languageCode)
      ? currentLanguages.filter(lang => lang !== languageCode)
      : [...currentLanguages, languageCode];
    
    updateField('languageSettingsComponent', {
      ...languageData,
      targetLanguages: updatedLanguages
    });
  };

  // 태그 추가
  const handleAddTag = () => {
    if (tagKeyInput.trim() && tagValueInput.trim()) {
      const newTag = {
        id: Date.now().toString(),
        key: tagKeyInput.trim(),
        value: tagValueInput.trim()
      };
      
      const updatedTags = [...(languageData.tags || []), newTag];
      updateField('languageSettingsComponent', {
        ...languageData,
        tags: updatedTags
      });
      
      setTagKeyInput('');
      setTagValueInput('');
    }
  };

  // 태그 제거
  const removeTag = (tagId: string) => {
    const updatedTags = (languageData.tags || []).filter(tag => tag.id !== tagId);
    updateField('languageSettingsComponent', {
      ...languageData,
      tags: updatedTags
    });
  };

  // 메모 업데이트
  const updateMemo = (newMemo: string) => {
    updateField('languageSettingsComponent', {
      ...languageData,
      memo: newMemo
    });
  };
  
  
  return (
    <div className="w-full max-w-none">
      {!hideTitle && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
      )}
      <div className="space-y-6">
        {/* 원본언어 섹션 */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">원본언어</Label>
          <p className="text-sm text-muted-foreground">
            {isLanguageDisabled 
              ? "등록된 작업의 원본언어입니다 (수정 불가)"
              : "번역할 소스 언어를 선택하세요 (1개만 선택)"
            }
          </p>
          <div className="grid grid-cols-3 gap-4">
            {languages.map(lang => {
              const isSelected = languageData.sourceLanguage === lang.code;
              const isDisabled = isLanguageDisabled;
              
              return (
                <div 
                  key={`source-${lang.code}`} 
                  className={`flex items-center space-x-2 p-2 rounded-md border transition-colors min-h-[40px] ${
                    isSelected && isDisabled
                      ? 'bg-orange-50 border-orange-200' 
                      : isSelected 
                        ? 'bg-blue-50 border-blue-200'
                        : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <Checkbox
                    id={`source-${lang.code}`}
                    checked={isSelected}
                    onCheckedChange={() => !isDisabled && selectSourceLanguage(lang.code)}
                    disabled={isDisabled}
                    className={isSelected && isDisabled ? 'data-[state=checked]:bg-orange-600' : ''}
                  />
                  <Label 
                    htmlFor={`source-${lang.code}`} 
                    className={`font-normal flex-1 flex items-center justify-between ${
                      isDisabled 
                        ? 'cursor-not-allowed text-muted-foreground' 
                        : 'cursor-pointer'
                    } ${
                      isSelected && isDisabled 
                        ? 'text-orange-700 font-medium' 
                        : isSelected 
                          ? 'text-blue-700 font-medium'
                          : ''
                    }`}
                  >
                    <span>{lang.name}</span>
                    <span className="w-16 flex justify-end">
                      {isSelected && isDisabled && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                          등록작업
                        </span>
                      )}
                    </span>
                  </Label>
                </div>
              );
            })}
          </div>
          {!languageData.sourceLanguage && !isLanguageDisabled && (
            <p className="text-sm text-red-500">원본언어를 선택해주세요.</p>
          )}
        </div>

        {/* 번역언어 섹션 */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">번역언어</Label>
          <p className="text-sm text-muted-foreground">
            {preSelectedTargetLanguages.length > 0
              ? "등록된 작업의 번역언어들이 선택되어 있습니다. 추가 언어를 선택할 수 있습니다."
              : "번역할 타겟 언어를 선택하세요 (복수 선택 가능)"
            }
          </p>
          <div className="grid grid-cols-3 gap-4">
            {languages.map(lang => {
              const isSourceLanguage = languageData.sourceLanguage === lang.code;
              const isPreSelected = preSelectedTargetLanguages.includes(lang.code) || preSelectedTargetLanguages.includes(lang.name);
              const isDisabled = isSourceLanguage || isPreSelected;
              
              return (
                <div 
                  key={`target-${lang.code}`} 
                  className={`flex items-center space-x-2 p-2 rounded-md border transition-colors min-h-[40px] ${
                    isPreSelected && !isSourceLanguage 
                      ? 'bg-blue-50 border-blue-200' 
                      : isSourceLanguage 
                        ? 'bg-gray-50 border-gray-200 opacity-50' 
                        : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <Checkbox
                    id={`target-${lang.code}`}
                    checked={languageData.targetLanguages.includes(lang.code)}
                    onCheckedChange={() => !isDisabled && toggleTargetLanguage(lang.code)}
                    disabled={isDisabled}
                    className={isPreSelected && !isSourceLanguage ? 'data-[state=checked]:bg-blue-600' : ''}
                  />
                  <Label 
                    htmlFor={`target-${lang.code}`} 
                    className={`font-normal flex-1 flex items-center justify-between ${
                      isDisabled 
                        ? 'cursor-not-allowed text-muted-foreground' 
                        : 'cursor-pointer'
                    } ${
                      isPreSelected && !isSourceLanguage 
                        ? 'text-blue-700 font-medium' 
                        : ''
                    }`}
                  >
                    <span>
                      {lang.name}
                      {isSourceLanguage && ' (원본언어)'}
                    </span>
                    <span className="w-16 flex justify-end">
                      {isPreSelected && !isSourceLanguage && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          등록작업
                        </span>
                      )}
                    </span>
                  </Label>
                </div>
              );
            })}
          </div>
          {languageData.targetLanguages.length === 0 && (
            <p className="text-sm text-red-500">최소 1개 이상의 번역언어를 선택해주세요.</p>
          )}
        </div>
        
        {/* 메모 섹션 */}
        <div className="space-y-3">
          <Label htmlFor="memo" className="text-base font-semibold">메모</Label>
          <p className="text-sm text-muted-foreground">
            작업에 대한 추가 정보나 주의사항을 입력하세요
          </p>
          <Textarea
            id="memo"
            value={languageData.memo}
            onChange={(e) => updateMemo(e.target.value)}
            placeholder="메모를 입력하세요..."
            rows={4}
          />
        </div>
      </div>
    </div>
  );
};